import { useState, useMemo } from 'react';
import TileCard from '../components/dashboard/TileCard';
import AllocationPie from '../components/dashboard/AllocationPie';
import NetworthChart from '../components/dashboard/NetworthChart';
import EntityCard from '../components/dashboard/EntityCard';
import PlanReminder from '../components/PlanReminder';
import AnimatedNumber from '../components/AnimatedNumber';
import { buildForecast } from '../lib/portfolio';
import { memberLabel } from '../lib/theme';

function fmt(v, decimals = 0) {
  if (!v) return '₹0';
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: decimals })}`;
}
function fmtUSD(v) {
  if (!v) return '$0';
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${Math.round(v).toLocaleString('en-US')}`;
}

export default function Dashboard({ data, savedPlan, savedAt, members }) {
  // Forecast like the Actuals tab: corpus + planned monthly @ blended CAGR, year-pickable.
  const plannedMonthly = savedPlan?.monthlyBase ?? 200000;
  const [fcYear, setFcYear] = useState(2030);
  const fcRows = useMemo(() => (data && !data.empty) ? buildForecast({
    startDate: data.latest_month, opening: data.networth, monthlyBase: plannedMonthly,
    annualCagr: data.cagr, months: 600,
  }) : [], [data, plannedMonthly]);
  const fcStartYear = data?.latest_month ? Number(data.latest_month.slice(0, 4)) : 2026;
  const fcInr = useMemo(() => {
    const r = [...fcRows].reverse().find(x => new Date(x.date + 'T00:00:00Z').getUTCFullYear() === fcYear);
    return r ? r.closing : null;
  }, [fcRows, fcYear]);

  if (!data || data.empty) return (
    <div className="flex flex-col items-center justify-center gap-4" style={{ height: '70vh' }}>
      <p style={{ color: 'var(--text-dim)' }}>No data yet.</p>
      <a href="/actual" className="px-5 py-2.5 rounded-xl text-sm font-semibold"
        style={{ background: 'var(--accent-grad)', color: '#fff' }}>Update Actuals →</a>
    </div>
  );

  const sp = data.sparklines || {};

  const glassPanel = {
    background: 'var(--card-grad)',
    border: '1px solid rgba(255,255,255,0.09)',
    boxShadow: '0 10px 30px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)',
    backdropFilter: 'blur(14px)',
  };

  return (
    <div className="min-h-screen lg:flex lg:flex-col [@media(min-width:1024px)_and_(min-height:880px)]:h-[calc(100dvh-64px)] [@media(min-width:1024px)_and_(min-height:880px)]:min-h-0 [@media(min-width:1024px)_and_(min-height:880px)]:overflow-hidden px-4 lg:px-8 py-6 lg:py-5" style={{ background: 'radial-gradient(ellipse 80% 50% at 80% -5%, var(--accent-glow, rgba(200,90,20,0.22)) 0%, transparent 55%), var(--bg, #131216)' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 lg:mb-3">
        <div className="flex items-center gap-3">
          {(() => {
            const name = memberLabel('PRANAV', members);
            const avatar = members?.PRANAV?.avatar;
            return avatar ? (
              <img src={avatar} alt={name}
                className="w-16 h-16 lg:w-20 lg:h-20 rounded-full object-cover flex-shrink-0"
                style={{ border: '2.5px solid var(--accent-glow, rgba(249,115,22,0.5))', boxShadow: '0 0 18px var(--accent-glow)' }} />
            ) : (
              <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full flex items-center justify-center text-2xl font-bold font-display flex-shrink-0"
                style={{ background: 'var(--accent-grad)', color: '#fff', boxShadow: '0 0 18px var(--accent-glow)' }}>
                {name.charAt(0).toUpperCase()}
              </div>
            );
          })()}
          <div>
          <h1 className="text-2xl font-bold font-display" style={{ color: 'var(--text)' }}>{memberLabel('PRANAV', members)}'s Portfolio Overview</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-mute)' }}>
            {data.latest_month ? `Updated ${new Date(data.latest_month).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}` : ''}
            {' · '}CAGR <span style={{ color: '#fb923c', fontWeight: 600 }}>{(data.cagr * 100).toFixed(2)}%</span>
          </p>
          </div>
        </div>
      </div>

      {/* Drift reminder vs saved plan */}
      <PlanReminder actualMatrix={data.matrix} savedPlan={savedPlan} meta={data.asset_meta} entities={data.entity_order} savedAt={savedAt} compact />

      {/* Row 1: 4 Tile Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <TileCard label="Available Cash" value={<AnimatedNumber value={data.cash_inr} format={fmt} />} sparkData={sp.cash} color="#34d399" glow="rgba(52,211,153,0.22)" green />
        <TileCard label="Indian Stocks" value={<AnimatedNumber value={data.indian_stocks_inr} format={fmt} />} sparkData={sp.indian_stocks} color="#60a5fa" glow="rgba(96,165,250,0.22)" />
        <TileCard
          label="US Stocks"
          value={<AnimatedNumber value={data.us_stocks_usd} format={fmtUSD} />}
          subValue={<AnimatedNumber value={data.us_stocks_inr} format={fmt} />}
          badge={`$1 = ₹${data.usd_inr?.toFixed(2)}`}
          sparkData={sp.us_stocks}
          color="#c084fc"
          glow="rgba(192,132,252,0.22)"
        />
        <TileCard label={data.debt_label || 'DSOP'} value={<AnimatedNumber value={data.dsop_inr} format={fmt} />} sparkData={sp.dsop} color="#fbbf24" glow="rgba(251,191,36,0.2)" />
      </div>

      {/* Row 2: Pie + Networth+Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-4 lg:flex-1 lg:min-h-0">
        {/* Allocation Pie */}
        <div className="lg:col-span-2 lg:min-h-0">
          <AllocationPie data={data.allocation_detail} />
        </div>
        {/* Combined Networth + Chart */}
        <div className="dash-card relative lg:col-span-3 rounded-2xl p-6 overflow-hidden flex flex-col lg:min-h-0"
          style={{ ...glassPanel, '--card-accent': '#34d399', '--card-accent-glow': 'rgba(52,211,153,0.22)' }}>
          <div className="card-glow" style={{ position: 'absolute', top: '-60px', right: '-40px', width: '260px', height: '200px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(52,211,153,0.16) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div className="relative grid grid-cols-2 gap-4 mb-5">
            <div>
              <p className="mb-1.5 font-medium" style={{ color: 'var(--text-dim)', fontSize: '15px' }}>Combined Networth</p>
              <p className="font-bold tabular-nums font-display" style={{ color: 'var(--text)', fontSize: '36px' }}><AnimatedNumber value={data.networth} format={fmt} /></p>
              <p className="font-semibold mt-1" style={{ color: 'var(--pos)', fontSize: '16px' }}><AnimatedNumber value={data.networth_usd} format={fmtUSD} /></p>
            </div>
            <div>
              <p className="mb-1.5 font-medium flex items-center gap-2" style={{ color: 'var(--text-dim)', fontSize: '15px' }}>
                Forecasted ·
                <span className="inline-flex items-center gap-1">
                  <button onClick={() => setFcYear(y => Math.max(fcStartYear, y - 1))} style={{ color: 'var(--accent)', fontWeight: 700 }}>‹</button>
                  <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{fcYear}</span>
                  <button onClick={() => setFcYear(y => y + 1)} style={{ color: 'var(--accent)', fontWeight: 700 }}>›</button>
                </span>
              </p>
              <p className="font-bold tabular-nums font-display" style={{ color: 'var(--text)', fontSize: '36px' }}>{fcInr != null ? <AnimatedNumber value={fcInr} format={fmt} /> : '—'}</p>
              <p className="font-semibold mt-1" style={{ color: 'var(--pos)', fontSize: '16px' }}>{fcInr != null ? <AnimatedNumber value={fcInr / (data.usd_inr || 84)} format={fmtUSD} /> : ''}</p>
            </div>
          </div>
          <div className="relative flex-1 min-h-0">
            <NetworthChart pranav={sp.pranav} rashu={sp.rashu} huf={sp.huf} combined={sp.combined}
              names={{ pranav: memberLabel('PRANAV', members), rashu: memberLabel('RASHU', members) }} />
          </div>
        </div>
      </div>

      {/* Row 3: Entity Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <EntityCard
          label={`${memberLabel('PRANAV', members)}'s Current Networth`}
          value={<AnimatedNumber value={data.pranav_networth} format={fmt} />}
          usdValue={<AnimatedNumber value={data.pranav_usd} format={fmtUSD} />}
          sparkData={sp.pranav}
          color="#60a5fa"
          glow="rgba(96,165,250,0.16)"
        />
        <EntityCard
          label={`${memberLabel('RASHU', members)}'s Current Networth`}
          value={<AnimatedNumber value={data.rashu_networth} format={fmt} />}
          usdValue={<AnimatedNumber value={data.rashu_usd} format={fmtUSD} />}
          sparkData={sp.rashu}
          color="#c084fc"
          glow="rgba(192,132,252,0.16)"
        />
        <EntityCard
          label="HUF Current Networth"
          value={<AnimatedNumber value={data.huf_networth} format={fmt} />}
          usdValue={<AnimatedNumber value={data.huf_usd} format={fmtUSD} />}
          sparkData={sp.huf}
          color="#fbbf24"
          glow="rgba(251,191,36,0.16)"
        />
      </div>
    </div>
  );
}
